(() => {
  if (window.HCKEventArchive) return;
  window.HCKEventArchive = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  document.documentElement.classList.add('hck-event-archive-js');

  class HckEventWave extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.canvas = this.querySelector('canvas');
      if (!this.canvas) return;

      this.context = this.canvas.getContext('2d');
      this.phase = 0;
      this.pointer = 0.5;
      this.targetAmplitude = 0.34;
      this.amplitude = 0.34;
      this.visible = true;
      this.resize = this.resize.bind(this);
      this.draw = this.draw.bind(this);
      this.handlePointer = this.handlePointer.bind(this);
      this.handleLeave = this.handleLeave.bind(this);

      this.resizeObserver = new ResizeObserver(this.resize);
      this.resizeObserver.observe(this);

      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.visible = entry.isIntersecting;
          if (this.visible && !this.frame) this.frame = requestAnimationFrame(this.draw);
        }, { rootMargin: '100px' });
        this.visibilityObserver.observe(this);
      }

      if (finePointer.matches && !reduceMotion.matches) {
        this.addEventListener('pointermove', this.handlePointer);
        this.addEventListener('pointerleave', this.handleLeave);
      }

      this.resize();
      this.frame = requestAnimationFrame(this.draw);
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.removeEventListener('pointermove', this.handlePointer);
      this.removeEventListener('pointerleave', this.handleLeave);
    }

    resize() {
      const bounds = this.getBoundingClientRect();
      const ratio = Math.min(window.devicePixelRatio || 1, 2);
      this.width = Math.max(1, Math.round(bounds.width));
      this.height = Math.max(1, Math.round(bounds.height));
      this.canvas.width = Math.round(this.width * ratio);
      this.canvas.height = Math.round(this.height * ratio);
      this.canvas.style.width = `${this.width}px`;
      this.canvas.style.height = `${this.height}px`;
      this.context.setTransform(ratio, 0, 0, ratio, 0, 0);
    }

    handlePointer(event) {
      const bounds = this.getBoundingClientRect();
      this.pointer = Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width));
      const vertical = Math.abs((event.clientY - bounds.top) / bounds.height - 0.5);
      this.targetAmplitude = Math.max(0.42, 0.86 - vertical * 0.55);
    }

    handleLeave() {
      this.targetAmplitude = 0.34;
      this.pointer = 0.5;
    }

    draw() {
      this.frame = null;
      if (!this.visible || !this.context) return;

      const ctx = this.context;
      const width = this.width;
      const height = this.height;
      ctx.clearRect(0, 0, width, height);

      this.amplitude += (this.targetAmplitude - this.amplitude) * 0.045;
      if (!reduceMotion.matches) this.phase += 0.008;

      const centerY = height * 0.53;
      const baseAmplitude = Math.min(height * 0.18, 38);
      const lines = [
        { color: 'rgba(49, 86, 164, 0.34)', width: 1.1, offset: 0, scale: 1 },
        { color: 'rgba(119, 188, 232, 0.22)', width: 0.8, offset: 1.6, scale: 0.64 },
        { color: 'rgba(49, 86, 164, 0.09)', width: 0.7, offset: 3.1, scale: 1.28 }
      ];

      lines.forEach((line) => {
        ctx.beginPath();
        ctx.strokeStyle = line.color;
        ctx.lineWidth = line.width;

        for (let x = 0; x <= width; x += 3) {
          const normalized = x / width;
          const distance = Math.abs(normalized - this.pointer);
          const localResponse = 0.35 + Math.exp(-distance * distance * 22) * this.amplitude;
          const envelope = Math.sin(Math.PI * normalized);
          const wave =
            Math.sin(normalized * Math.PI * 7 + this.phase * 18 + line.offset) * 0.68 +
            Math.sin(normalized * Math.PI * 17 - this.phase * 10 + line.offset) * 0.32;
          const y = centerY + wave * baseAmplitude * envelope * localResponse * line.scale;
          if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
        }

        ctx.stroke();
      });

      if (!reduceMotion.matches || Math.abs(this.amplitude - this.targetAmplitude) > 0.005) {
        this.frame = requestAnimationFrame(this.draw);
      }
    }
  }

  class HckEventCountdown extends HTMLElement {
    connectedCallback() {
      if (this.timer) return;
      this.end = new Date(this.dataset.endTime).getTime();
      if (!Number.isFinite(this.end)) {
        this.hidden = true;
        return;
      }
      this.update();
      this.timer = window.setInterval(() => this.update(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    update() {
      const remaining = Math.max(0, this.end - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const values = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60
      };

      Object.entries(values).forEach(([key, value]) => {
        const target = this.querySelector(`[data-${key}]`);
        if (target) target.textContent = String(value).padStart(2, '0');
      });

      if (remaining === 0) {
        window.clearInterval(this.timer);
        this.timer = null;
        this.classList.add('is-ended');
      }
    }
  }

  if (!customElements.get('hck-event-wave')) customElements.define('hck-event-wave', HckEventWave);
  if (!customElements.get('hck-event-countdown')) customElements.define('hck-event-countdown', HckEventCountdown);

  const initializeReveal = (scope = document) => {
    const elements = scope.querySelectorAll('.hck-archive-reveal:not([data-hck-reveal-ready])');
    elements.forEach((element) => {
      element.dataset.hckRevealReady = 'true';
      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        element.classList.add('is-visible');
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -6% 0px' });
      observer.observe(element);
    });
  };

  const initializeCards = (scope = document) => {
    const cards = scope.querySelectorAll('[data-hck-event-card]:not([data-hck-card-ready])');
    cards.forEach((card) => {
      card.dataset.hckCardReady = 'true';

      if ('IntersectionObserver' in window) {
        const activeObserver = new IntersectionObserver(([entry]) => {
          card.classList.toggle('is-active', entry.isIntersecting);
        }, { threshold: 0.38 });
        activeObserver.observe(card);
      }

      if (!finePointer.matches || reduceMotion.matches) return;
      let frame;
      card.addEventListener('pointermove', (event) => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const media = card.querySelector('.hck-event-card__media');
          if (!media) return;
          const bounds = media.getBoundingClientRect();
          const x = Math.max(0, Math.min(100, ((event.clientX - bounds.left) / bounds.width) * 100));
          const y = Math.max(0, Math.min(100, ((event.clientY - bounds.top) / bounds.height) * 100));
          card.style.setProperty('--hck-pointer-x', `${x}%`);
          card.style.setProperty('--hck-pointer-y', `${y}%`);
        });
      });

      card.addEventListener('pointerleave', () => {
        card.style.setProperty('--hck-pointer-x', '50%');
        card.style.setProperty('--hck-pointer-y', '50%');
      });
    });
  };

  const initializeFooterGate = (scope = document) => {
    const gate = scope.matches?.('[data-hck-site-footer-gate]')
      ? scope
      : scope.querySelector?.('[data-hck-site-footer-gate]');
    if (!gate || gate.dataset.hckFooterGateReady === 'true') return;

    const siteFooter = document.querySelector('m-footer');
    if (!siteFooter) return;

    gate.dataset.hckFooterGateReady = 'true';
    const footerTarget = siteFooter.closest('.shopify-section') || siteFooter;
    footerTarget.classList.add('hck-site-footer--event-gated');

    const revealFooter = () => {
      footerTarget.classList.add('is-revealed');
    };

    if (!('IntersectionObserver' in window)) {
      revealFooter();
      return;
    }

    const gateObserver = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      revealFooter();
      gateObserver.disconnect();
    }, { threshold: 0.18 });

    gateObserver.observe(gate);
  };

  const initialize = (scope = document) => {
    initializeReveal(scope);
    initializeCards(scope);
    initializeFooterGate(scope);
  };

  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
